import { autorun, makeAutoObservable } from 'mobx';
import { inject, injectable } from 'inversify';
import { clearPersistedStore, isHydrated, makePersistable } from 'mobx-persist-store';
import { PATH_CABINET } from '@app/routes/paths';
import { HistoryModule } from '@mobx/services/history';
import { type AuthService, AuthServiceIdentifier } from '@shared/types/mobx/services/auth';
import { type RolesService, type UserRole } from '@shared/types/mobx/services/roles';

@injectable()
export class RolesServiceImpl implements RolesService {
  roles: UserRole[] = [];

  currentRole: UserRole | null = null;

  constructor(@inject(AuthServiceIdentifier) private authModule: AuthService, private history: HistoryModule) {
    makeAutoObservable(this);
    makePersistable(this, { name: 'RolesModule', properties: ['roles', 'currentRole'] });

    autorun(() => {
      this.roles = this.authModule.userData?.roles ?? [];
    });
  }

  get isHasAdminRole() {
    return !!this.roles.find((role) => role.role === 'ADMIN');
  }

  get isHasOperatorRole() {
    return !!this.roles.find((role) => role.role === 'OPERATOR');
  }

  /**
   * Завершилась ли гидратация persist-стора (дефект «сброс роли на ABONENT после очистки сессии»).
   *
   * `currentRole` восстанавливается из localStorage АСИНХРОННО (`makePersistable`), поэтому на
   * первом рендере он `null` даже у пользователя с выбранной ролью. Гвард «роль выбрана»
   * (app/routes/guards/RoleSelectedGuard.tsx) принимает решение только после гидрации.
   * Реактивность — как у `OsiServiceImpl.isStoreHydrated`: `isHydrated()` читает observable-поле
   * PersistStore, геттер становится computed и перевычисляется у observer-компонентов.
   */
  get isStoreHydrated(): boolean {
    return isHydrated(this);
  }

  /**
   * @param withRedirect по умолчанию `true` — штатный флоу экрана выбора роли и регистрации:
   * выбрали роль → /cabinet. `false` — только запомнить роль, без навигации: так гвард
   * «роль выбрана» автоматически выбирает единственную доступную роль и оставляет пользователя
   * на открытой странице (иначе была бы двойная навигация и уход с /osi/* на /cabinet).
   */
  selectRole(role: UserRole, withRedirect = true) {
    this.currentRole = role;

    if (withRedirect) this.history.navigateTo(PATH_CABINET.root);
  }

  // Чистим persist сессии: сбрасываем roles/currentRole к initial, затем удаляем localStorage-ключ.
  // Иначе старая выданная/прежняя роль протекает между входами (кабинет остаётся «Абонентом»).
  clearSession = async () => {
    this.roles = [];
    this.currentRole = null;
    await clearPersistedStore(this);
  };
}
